import dayjs from '~/timeInit';

import { SYDNEY_TIMEZONE } from '~/services/TimeService';
import type { AdminChatConversationDetails } from '~/services/TripPlannerService';

export const DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS = 30;
export const TRIP_PLANNER_CHAT_STATS_TIMEZONE = SYDNEY_TIMEZONE;

export interface TripPlannerChatStatsPoint {
  date: string;
  label: string;
  conversations: number;
}

export interface TripPlannerChatStatsUserDistributionPoint {
  bucket: string;
  label: string;
  users: number;
}

export interface TripPlannerChatStatsSummary {
  today: number;
  last7Days: number;
  last30Days: number;
  averagePerDay: number;
}

export interface TripPlannerChatStatsResult {
  chartData: Array<TripPlannerChatStatsPoint>;
  userDistributionChartData: Array<TripPlannerChatStatsUserDistributionPoint>;
  summary: TripPlannerChatStatsSummary;
}

type UserSummary = {
  email?: string | null;
};

const USER_TRIPS_BUCKETS = [
  {
    bucket: '1',
    label: '1',
    matches: (tripCount: number) => tripCount === 1,
  },
  {
    bucket: '2',
    label: '2',
    matches: (tripCount: number) => tripCount === 2,
  },
  {
    bucket: '3-4',
    label: '3-4',
    matches: (tripCount: number) => tripCount >= 3 && tripCount <= 4,
  },
  {
    bucket: '5-9',
    label: '5-9',
    matches: (tripCount: number) => tripCount >= 5 && tripCount <= 9,
  },
  {
    bucket: '10+',
    label: '10+',
    matches: (tripCount: number) => tripCount >= 10,
  },
] as const;

function getUserTripsBucket(tripCount: number) {
  return (
    USER_TRIPS_BUCKETS.find((bucket) => bucket.matches(tripCount)) ?? USER_TRIPS_BUCKETS[USER_TRIPS_BUCKETS.length - 1]
  );
}

export function isLuxuryEscapesInternalEmail(email?: string | null) {
  return email?.toLowerCase().endsWith('@luxuryescapes.com') ?? false;
}

export function filterLuxuryEscapesInternalConversations(
  conversations: Array<AdminChatConversationDetails>,
  userSummariesById: Map<string, UserSummary>,
) {
  return conversations.filter((conversation) => {
    const userSummary = userSummariesById.get(conversation.createdBy);

    return !isLuxuryEscapesInternalEmail(userSummary?.email);
  });
}

export function buildTripPlannerChatStats(
  conversations: Array<AdminChatConversationDetails>,
  days = DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS,
  now = new Date(),
): TripPlannerChatStatsResult {
  const startDate = dayjs(now)
    .tz(TRIP_PLANNER_CHAT_STATS_TIMEZONE)
    .startOf('day')
    .subtract(days - 1, 'day');
  const dailyCounts = new Map<string, number>();
  const userTripCounts = new Map<string, number>();

  conversations.forEach((conversation) => {
    const createdAt = dayjs(conversation.createdAt).tz(TRIP_PLANNER_CHAT_STATS_TIMEZONE);
    if (createdAt.isBefore(startDate)) {
      return;
    }

    const dateKey = createdAt.format('YYYY-MM-DD');
    dailyCounts.set(dateKey, (dailyCounts.get(dateKey) ?? 0) + 1);
    userTripCounts.set(conversation.createdBy, (userTripCounts.get(conversation.createdBy) ?? 0) + 1);
  });

  const chartData = Array.from({ length: days }, (_, index) => {
    const date = startDate.add(index, 'day');
    const dateKey = date.format('YYYY-MM-DD');

    return {
      date: dateKey,
      label: date.format('D MMM'),
      conversations: dailyCounts.get(dateKey) ?? 0,
    };
  });

  const last30Days = chartData.reduce((sum, point) => sum + point.conversations, 0);
  const last7Days = chartData.slice(-7).reduce((sum, point) => sum + point.conversations, 0);
  const averagePerDay = Number((last30Days / days).toFixed(1));
  const tripsPerUserDistribution = new Map<string, number>();

  userTripCounts.forEach((tripCount) => {
    const bucket = getUserTripsBucket(tripCount);

    tripsPerUserDistribution.set(bucket.bucket, (tripsPerUserDistribution.get(bucket.bucket) ?? 0) + 1);
  });

  const userDistributionChartData = USER_TRIPS_BUCKETS.map((bucket) => ({
    bucket: bucket.bucket,
    label: bucket.label,
    users: tripsPerUserDistribution.get(bucket.bucket) ?? 0,
  })).filter((point) => point.users > 0);

  return {
    chartData,
    userDistributionChartData,
    summary: {
      today: chartData[chartData.length - 1]?.conversations ?? 0,
      last7Days,
      last30Days,
      averagePerDay,
    },
  };
}
